import { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Calendar, Clock, Facebook, Linkedin } from 'lucide-react';
import { getPostBySlug, getRelatedPosts } from './BlogIndex';
import * as BlogPosts from './blog-posts';

export default function NewsroomDetail() {
    const { slug } = useParams();
    const navigate = useNavigate();

    const post = getPostBySlug(slug);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [slug]);

    const handleBackToList = () => {
        navigate('/newsroom');
    };

    const handleRelatedPostClick = (postSlug) => {
        navigate(`/newsroom/${postSlug}`);
    };

    if (!post) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold text-white mb-4">Article Not Found</h1>
                    <p className="text-slate-400 mb-6">The article you're looking for doesn't exist.</p>
                    <button
                        onClick={handleBackToList}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Newsroom
                    </button>
                </div>
            </div>
        );
    }

    const ContentComponent = BlogPosts[post.contentFile];

    if (!ContentComponent) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <h1 className="text-4xl font-bold text-white mb-4">Content Not Available</h1>
                    <p className="text-slate-400 mb-6">The content for this article is currently unavailable.</p>
                    <button
                        onClick={handleBackToList}
                        className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                        Back to Newsroom
                    </button>
                </div>
            </div>
        );
    }

    const relatedPosts = getRelatedPosts(post).reverse();

    return (
        <div className="min-h-screen">
            {/* Back Button */}
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-8">
                <button
                    onClick={handleBackToList}
                    className="flex items-center gap-2 text-blue-400 hover:text-blue-300 transition-colors mb-8 group"
                >
                    <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
                    Back to Newsroom
                </button>
            </div>

            {/* Article Header */}
            <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
                <div className="mb-4">
                    <span className="inline-block px-4 py-1 rounded-full text-sm font-medium bg-blue-600/20 text-blue-400 border border-blue-500/30">
                        {post.category}
                    </span>
                </div>

                <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
                    {post.title}
                </h1>

                <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-8 pb-8 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <User className="w-5 h-5" />
                        <span>{post.author}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5" />
                        <span>{post.date}</span>
                    </div>
                    <div className="flex items-center gap-2">
                        <Clock className="w-5 h-5" />
                        <span>{post.readTime}</span>
                    </div>
                </div>

                <div className="relative h-[30rem] rounded-2xl overflow-hidden mb-12 shadow-2xl">
                    <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent"></div>
                </div>

                <div 
                    className="prose prose-invert prose-lg max-w-none
                        prose-headings:text-white prose-headings:font-bold
                        prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-6
                        prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-4
                        prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-6
                        prose-a:text-blue-400 prose-a:no-underline hover:prose-a:text-blue-300
                        prose-strong:text-white prose-strong:font-semibold
                        prose-ul:text-slate-300 prose-ul:my-6
                        prose-li:mb-2
                        prose-code:text-blue-400 prose-code:bg-slate-800 prose-code:px-2 prose-code:py-1 prose-code:rounded"
                >
                    <ContentComponent />
                </div>

                <div className="flex flex-wrap gap-2 mt-12 pt-8 border-t border-white/10">
                    {post.tags.map((tag) => (
                        <span
                            key={tag}
                            className="px-3 py-1 rounded-full text-sm bg-white/5 text-slate-400 border border-white/10 hover:border-blue-500/50 hover:text-blue-400 transition-colors cursor-pointer"
                        >
                            #{tag}
                        </span>
                    ))}
                </div>

                <div className="mt-12 p-6 rounded-xl bg-gradient-to-r from-purple-600/20 to-blue-600/20 backdrop-blur-sm border border-white/10">
                    <h3 className="text-white font-semibold mb-4">Share this article</h3>
                    <div className="flex gap-3">
                        <button 
                            onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${window.location.href}`, '_blank')}
                            className="p-3 rounded-lg bg-blue-600 hover:bg-blue-700 text-white transition-colors"
                            aria-label="Share on Facebook"
                        >
                            <Facebook className="w-5 h-5" />
                        </button>
                        <button 
                            onClick={() => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${window.location.href}&title=${post.title}`, '_blank')}
                            className="p-3 rounded-lg bg-blue-700 hover:bg-blue-800 text-white transition-colors"
                            aria-label="Share on LinkedIn"
                        >
                            <Linkedin className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </article>

            {/* Related Posts */}
            {relatedPosts.length > 0 && (
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 border-t border-white/10">
                    <h2 className="text-3xl font-bold text-white mb-8">Related Articles</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {relatedPosts.map((relatedPost) => (
                            <article
                                key={relatedPost.id}
                                onClick={() => handleRelatedPostClick(relatedPost.slug)}
                                className="group bg-slate-900/50 backdrop-blur-sm rounded-xl overflow-hidden border border-white/10 hover:border-blue-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-blue-500/20 hover:-translate-y-1 cursor-pointer"
                            >
                                <div className="relative h-48 overflow-hidden bg-gradient-to-r from-purple-600 to-blue-600">
                                    <img
                                        src={relatedPost.image}
                                        alt={relatedPost.title}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-slate-900/20 to-transparent"></div>
                                </div>
                                <div className="p-6">
                                    <time className="text-xs text-blue-400 font-medium tracking-wide">
                                        {relatedPost.date}
                                    </time>
                                    <h3 className="text-xl font-bold text-white mt-3 mb-2 line-clamp-2 group-hover:text-blue-400 transition-colors">
                                        {relatedPost.title}
                                    </h3>
                                    <p className="text-sm text-slate-400 line-clamp-2">
                                        {relatedPost.description}
                                    </p>
                                </div>
                            </article>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}